import { Box, Card, Flex, Text } from '@mantine/core';
import { GetEnergyConsumptionResponse } from 'api/actions/get-energy-consumption/get-energy-consumption-response';
import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend, TooltipItem } from 'chart.js';
import theme from 'theme';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import { xScaleConfig, yGridConfig, barDatasetBase } from 'pages/tuya-device/components/chartHelpers';
import useLang from 'lang';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface ConsumptionChartCardProps {
  /** Full daily energy consumption response */
  data: GetEnergyConsumptionResponse;
  /** Total consumption in Wh for the selected day */
  totalConsumption: number;
}

/**
 * Displays a bar chart of real electricity consumption (Wh) per 15-minute interval.
 *
 * @prop {GetEnergyConsumptionResponse} data - Daily consumption data with intervals.
 * @prop {number} totalConsumption - Total Wh consumed during the day.
 */
export default function ConsumptionChartCard({ data, totalConsumption }: ConsumptionChartCardProps) {
  const { _t } = useLang();
  const validIntervals = useMemo(() => data.intervals.filter((i) => i.consumption !== null), [data.intervals]);

  const chartData = useMemo(
    () => ({
      labels: validIntervals.map((i) => i.label),
      datasets: [
        {
          ...barDatasetBase,
          label: _t('Consumption (Wh)'),
          data: validIntervals.map((i) => i.consumption || 0),
          backgroundColor: '#551FFF',
          hoverBackgroundColor: '#4016CC',
        },
      ],
    }),
    [_t, validIntervals]
  );

  const chartOptions = useMemo(() => {
    const values = validIntervals.map((i) => i.consumption || 0);
    const average = values.length ? values.reduce((s, v) => s + v, 0) / values.length : 0;
    const minValue = values.length ? Math.min(...values) : 0;
    const yMin = values.length ? Math.floor((average - (average - minValue) * 1.2) / 5) * 5 : 0;

    return {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: { ...xScaleConfig },
        y: {
          ticks: {
            color: theme.colors?.gray?.[7],
            callback: (v: number | string): string => `${v} Wh`,
          },
          grid: yGridConfig,
          min: values.length ? yMin : 0,
          beginAtZero: !values.length,
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (ctx: TooltipItem<'bar'>): string => `${_t('Consumption')}: ${ctx.parsed.y.toFixed(2)} Wh`,
          },
        },
      },
    };
  }, [_t, validIntervals]);

  return (
    <Card p={20} radius={10} shadow="sm">
      <H2SemiBold mb={20}>
        {_t('Energy consumption')} - {new Date(data.date).toLocaleDateString('sk-SK')}
      </H2SemiBold>
      <Box style={{ height: 400 }}>
        <Bar data={chartData} options={chartOptions} />
      </Box>

      <Box mt={20} p={15} style={{ backgroundColor: '#F8F9FA', borderRadius: 8, border: '1px solid #E9ECEF' }}>
        <Flex justify="space-between" align="center">
          <Text size="lg" fw={600} c="gray.7">
            {_t('Total daily consumption')}:
          </Text>
          <Text size="xl" fw={700} c="#551FFF">
            {(totalConsumption / 1000).toFixed(2)} kWh
          </Text>
        </Flex>
        <Text size="sm" c="gray.6" mt={5}>
          ({totalConsumption.toFixed(0)} Wh)
        </Text>
      </Box>
    </Card>
  );
}
